import { ApiClient, ApiError, type paths, type RequestOf, type ResponseOf } from '@platform/api-client';

/**
 * The panel is served from the same origin as the API, so the session cookie
 * travels without CORS and without relaxing SameSite. In development Vite
 * proxies /v1 to the API for exactly that reason.
 */
export const api = new ApiClient({
    onUnauthenticated: () => {
        // Broadcast rather than redirect here: routing is the router's job, and a
        // hard navigation would discard unsaved work in an open form.
        window.dispatchEvent(new CustomEvent('session:expired'));
    },
});

export { ApiError };

// Public branding served before a session exists. Kept a plain interface rather
// than derived from the schema so a consumer can read it without the generated
// types being regenerated first.
export interface AppConfig {
    ownStoreName: string;
    /** The terms of use and the privacy notice on the marketing site, which sign-up links (ADR 0018 §1). */
    termsUrl: string;
    privacyNoticeUrl: string;
}

export type Me = ResponseOf<'/v1/me', 'get'>;
export type SessionPayload = ResponseOf<'/v1/auth/login', 'post'>;
export type LoginBody = RequestOf<'/v1/auth/login', 'post'>;
export type RegisterBody = RequestOf<'/v1/auth/register', 'post'>;
export type AcceptTermsBody = RequestOf<'/v1/me/terms-acceptance', 'post'>;
export type Company = NonNullable<Me['companies']>[number];

export type Subscription = ResponseOf<'/v1/billing/subscription', 'get'>;
export type PlanRequestBody = RequestOf<'/v1/contact/plan-request', 'post'>;
export type SupportRequestBody = RequestOf<'/v1/contact/support', 'post'>;
export type PlanList = ResponseOf<'/v1/billing/plans', 'get'>;
export type PaymentOptions = ResponseOf<'/v1/billing/payment-options', 'get'>;
export type PurchaseBody = RequestOf<'/v1/billing/purchase', 'post'>;
export type Purchase = ResponseOf<'/v1/billing/purchase', 'post'>;
export type BillingCharges = ResponseOf<'/v1/billing/charges', 'get'>;
export type BillingCharge = BillingCharges['charges'][number];
export type BillingChargeInvoiceLink = ResponseOf<'/v1/billing/charges/{chargeId}/invoice', 'get'>;
/** The pre-information form and the distance contract written for one purchase (ADR 0018). */
export type BillingAgreement = ResponseOf<'/v1/billing/agreement', 'get'>;
export type BillingAgreementQuery = NonNullable<paths['/v1/billing/agreement']['get']['parameters']['query']>;
/** The texts a payment was accepted on, as they were kept. */
export type BillingChargeAgreement = ResponseOf<'/v1/billing/charges/{chargeId}/agreement', 'get'>;
export type PublicPlanList = ResponseOf<'/v1/public/plans', 'get'>;
export type Plan = PlanList['plans'][number];
export type PlanPrice = Plan['prices']['monthly'];
/** One limit the account's package sets, beside what the account uses of it (ADR 0017 §1). */
export type LimitState = Subscription['limitStates'][number];

// The bulk import endpoints move files, so they are outside the typed contract;
// this mirrors the JSON the import handler returns.
export interface ProductImportRow {
    /** The number Excel shows the row under. */
    line: number;
    sku: string;
    ok: boolean;
    /** The refusal's problem code: the page words it (ADR 0005). None for a failure of the server's own. */
    code?: string;
    /** The refusal's English detail, for integrators; never shown. */
    error?: string;
    /** What a row that adds a product brings (ADR 0019 §10): its product price in its currency, its opening stock and how many pictures it offers. */
    salePrice?: number;
    listPrice?: number;
    currency?: string;
    stock?: number;
    imageCount?: number;
}
export interface ProductImportResult {
    committed: boolean;
    /** What the file was read as (ADR 0019 §7); separator and encoding are a CSV's. */
    format: 'csv' | 'xlsx';
    separator?: 'semicolon' | 'comma' | 'tab';
    encoding?: 'utf-8' | 'utf-16' | 'windows-1254';
    total: number;
    created: number;
    failed: number;
    /** Rows naming a product the catalogue already holds, which the import leaves as it is. */
    existing: number;
    /** Rows the package's product limit leaves out (ADR 0017 §2). */
    overLimit: number;
    rows: ProductImportRow[];
}

/** One row of a listing sheet's preview or apply (ADR 0006 §1); mirrors the handler's JSON like the product import's. */
export interface ListingSheetRow {
    /** The number Excel shows the row under. */
    line: number;
    sku: string;
    title?: string;
    verdict: 'changed' | 'unchanged' | 'imported' | 'refused';
    /** A refused row's problem code: the page words it (ADR 0005). */
    code?: string;
    /** The refusal's English detail, for integrators; never shown. */
    error?: string;
    /** The row's stock was not read: the channel's stock is the shelf's. */
    stockIgnored?: boolean;
    oldPrice?: number;
    newPrice?: number;
    oldListPrice?: number;
    newListPrice?: number;
    currency?: string;
    oldStock?: number;
    newStock?: number;
}
export interface ListingSheetResult {
    committed: boolean;
    format: 'csv' | 'xlsx';
    separator?: 'semicolon' | 'comma' | 'tab';
    encoding?: 'utf-8' | 'utf-16' | 'windows-1254';
    /** The channel takes its stock from the shelf, so no row's stock was read. */
    stockFromShelf: boolean;
    total: number;
    changed: number;
    unchanged: number;
    imported: number;
    refused: number;
    rows: ListingSheetRow[];
}

export type BulkEdit = ResponseOf<'/v1/products/bulk-status', 'post'>;

export type QuestionList = ResponseOf<'/v1/questions', 'get'>;
export type Question = QuestionList['questions'][number];
/** One question with its conversation and what its marketplace accepts. */
export type QuestionDetail = ResponseOf<'/v1/questions/{questionId}', 'get'>;
export type QuestionMessage = NonNullable<QuestionDetail['messages']>[number];
export type CannedList = ResponseOf<'/v1/canned-answers', 'get'>;

export type WebhookList = ResponseOf<'/v1/webhooks', 'get'>;
export type Webhook = WebhookList['endpoints'][number];
export type WebhookDeliveryList = ResponseOf<'/v1/webhooks/deliveries', 'get'>;
export type WebhookDelivery = WebhookDeliveryList['deliveries'][number];

export type CouponList = ResponseOf<'/v1/storefront/coupons', 'get'>;
export type Coupon = CouponList['coupons'][number];
export type SaveCouponBody = RequestOf<'/v1/storefront/coupons', 'put'>;

export type PromotionList = ResponseOf<'/v1/storefront/promotions', 'get'>;
export type Promotion = PromotionList['promotions'][number];
export type SavePromotionBody = RequestOf<'/v1/storefront/promotions', 'post'>;
export type SizeGuideList = ResponseOf<'/v1/storefront/size-guides', 'get'>;
export type SizeGuide = SizeGuideList['sizeGuides'][number];
export type SaveSizeGuideBody = RequestOf<'/v1/storefront/size-guides', 'post'>;

export type NotificationList = ResponseOf<'/v1/notifications', 'get'>;
export type Notification = NotificationList['notifications'][number];
export type NotificationPreferences = ResponseOf<'/v1/me/notification-preferences', 'get'>;
export type SaveNotificationPreferencesBody = RequestOf<'/v1/me/notification-preferences', 'put'>;

export type ApiTokenList = ResponseOf<'/v1/api-tokens', 'get'>;
export type ApiToken = ApiTokenList['tokens'][number];
export type CreatedApiToken = ResponseOf<'/v1/api-tokens', 'post'>;

export type ProductList = ResponseOf<'/v1/products', 'get'>;
export type Product = ResponseOf<'/v1/products/{productId}', 'get'>;
export type UpdateProductVariantsBody = RequestOf<'/v1/products/{productId}/variants', 'patch'>;
export type AddProductVariantBody = RequestOf<'/v1/products/{productId}/variants', 'post'>;
export type DuplicateProductBody = RequestOf<'/v1/products/{productId}/duplicate', 'post'>;
export type DescriptionTemplateList = ResponseOf<'/v1/description-templates', 'get'>;
export type DescriptionTemplate = DescriptionTemplateList['templates'][number];
export type CreateDescriptionTemplateBody = RequestOf<'/v1/description-templates', 'post'>;
export type SetProductPricesBody = RequestOf<'/v1/products/{productId}/prices', 'put'>;
export type CreateProductBody = RequestOf<'/v1/products', 'post'>;
export type UpdateProductBody = RequestOf<'/v1/products/{productId}', 'put'>;
export type PublishProductBody = RequestOf<'/v1/products/{productId}/publish', 'post'>;
export type BulkDeleteProductsBody = RequestOf<'/v1/products/bulk-delete', 'post'>;
export type BrandList = ResponseOf<'/v1/brands', 'get'>;
export type Brand = ResponseOf<'/v1/brands', 'post'>;
export type CreateBrandBody = RequestOf<'/v1/brands', 'post'>;
// The company's brands, managed on Markalar (ADR 0022 §12).
export type BrandOverview = ResponseOf<'/v1/brand-overview', 'get'>;
export type BrandOverviewRow = BrandOverview['brands'][number];
export type BrandChannelState = BrandOverviewRow['channels'][number];
export type UpdateBrandBody = RequestOf<'/v1/brands/{brandId}', 'patch'>;
export type BrandMergeResult = ResponseOf<'/v1/brands/{brandId}/merge', 'post'>;
export type BrandLogo = ResponseOf<'/v1/brand-logos', 'post'>;
// Marketplace brands (ADR 0022 §8).
export type ChannelBrandSearch = ResponseOf<'/v1/channels/{channelKey}/brands', 'get'>;
export type ChannelBrand = ChannelBrandSearch['brands'][number];
export type ChannelBrandSuggestions = ResponseOf<'/v1/channels/{channelKey}/brand-suggestions', 'get'>;
export type BrandSearch = ResponseOf<'/v1/brand-search', 'get'>;
export type BrandSearchResult = BrandSearch['results'][number];
export type BrandMappingList = ResponseOf<'/v1/channels/{channelKey}/brand-mappings', 'get'>;
export type BrandMappingRow = BrandMappingList['mappings'][number];
export type BrandMappingState = NonNullable<BrandMappingRow['state']>;
export type MapBrandBody = RequestOf<'/v1/brands/{brandId}/mapping/{channelKey}', 'put'>;
export type BrandMapping = ResponseOf<'/v1/brands/{brandId}/mapping/{channelKey}', 'put'>;
export type AcceptBrandSuggestionsBody = RequestOf<'/v1/channels/{channelKey}/brand-mappings/accept', 'post'>;
export type AcceptBrandSuggestionsResult = ResponseOf<'/v1/channels/{channelKey}/brand-mappings/accept', 'post'>;
export type ProductChannelBrand = ResponseOf<'/v1/products/{productId}/channels/{channelKey}/brand', 'get'>;
export type SetProductChannelBrandBody = RequestOf<'/v1/products/{productId}/channels/{channelKey}/brand', 'put'>;
export type BrandRequestBody = RequestOf<'/v1/channel-accounts/{accountId}/brand-requests', 'post'>;
export type RequestedBrand = ResponseOf<'/v1/channel-accounts/{accountId}/brand-requests', 'post'>;
export type ProductSummary = ProductList['products'][number];
/** Where a product's offer on one sales channel stands, as its chip names it (ADR 0009 §2). */
export type ProductChannelState = ProductSummary['channels'][number]['state'];
export type CategoryList = ResponseOf<'/v1/categories', 'get'>;
export type Category = CategoryList['categories'][number];
export type RenameCategoryBody = RequestOf<'/v1/categories/{categoryId}', 'patch'>;

// Index signatures so a query object can be walked generically. Without one,
// the shared serialiser cannot accept a typed query at all.
export interface ProductQuery {
    search?: string;
    status?: ProductStatus;
    channelAccountId?: string;
    // The state of that channel's own offer. Needs the channel: a listing is
    // always a listing somewhere.
    listingStatus?: ListingState;
    unlisted?: boolean;
    // How ready a product is for one marketplace. The state needs the channel:
    // readiness is always readiness for somewhere.
    mappingChannelKey?: string;
    mappingState?: MappingState;
    sort?: ProductSort;
    limit?: number;
    offset?: number;
    [key: string]: string | number | boolean | undefined;
}

/** An order the product list can be read in: a column and a direction. */
export type ProductSort = NonNullable<NonNullable<paths['/v1/products']['get']['parameters']['query']>['sort']>;

/** An order the order list can be read in: a column and a direction. */
export type OrderSort = NonNullable<NonNullable<paths['/v1/orders']['get']['parameters']['query']>['sort']>;

/** An order the customer list can be read in: a column and a direction. */
export type CustomerSort = NonNullable<NonNullable<paths['/v1/customers']['get']['parameters']['query']>['sort']>;

/** An order the claim list can be read in: a column and a direction. */
export type ClaimSort = NonNullable<NonNullable<paths['/v1/claims']['get']['parameters']['query']>['sort']>;

/** An order the running-out stock list can be read in: a column and a direction. */
export type StockSort = NonNullable<NonNullable<paths['/v1/stock/low']['get']['parameters']['query']>['sort']>;

/** Where a product has got to on its way to one marketplace. */
export type MappingState = 'unmapped' | 'incomplete' | 'ready';

/** What one channel's offer of a product is doing, as the API names it. */
export type ListingState = NonNullable<NonNullable<paths['/v1/products']['get']['parameters']['query']>['listingStatus']>;

/** Where a product stands in the catalogue itself, whatever its channels say, as the API names it. */
export type ProductStatus = NonNullable<NonNullable<paths['/v1/products']['get']['parameters']['query']>['status']>;

function queryString(query: Record<string, unknown>): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        // A list repeats its key, which is how the API reads a parameter it
        // declares more than once — the invoice preview's rate per line.
        if (Array.isArray(value)) {
            for (const item of value) params.append(key, String(item));
            continue;
        }
        // false is dropped alongside empty: a filter that is off should not appear
        // in the URL at all, or every list request carries noise.
        if (value !== undefined && value !== '' && value !== false) {
            params.set(key, String(value));
        }
    }
    const encoded = params.toString();
    return encoded ? `?${encoded}` : '';
}

export type ChannelList = ResponseOf<'/v1/channels', 'get'>;
export type PublishResult = ResponseOf<'/v1/channel-accounts/{accountId}/publish', 'post'>;
// Kanalda Yayınla: the selected products published on one channel at what each
// offer there holds, each answered with its outcome (ADR 0006 §1).
export type PublishSelectionBody = RequestOf<'/v1/channel-accounts/{accountId}/publish-selection', 'post'>;
export type PublishSelectionResult = ResponseOf<'/v1/channel-accounts/{accountId}/publish-selection', 'post'>;
export type SelectionProduct = PublishSelectionResult['products'][number];
export type SelectionOutcome = SelectionProduct['outcome'];
export type WithdrawResult = ResponseOf<'/v1/products/{productId}/listings/{accountId}/withdraw', 'post'>;
export type MatchDecisionBody = RequestOf<'/v1/products/{productId}/listings/{accountId}/match-decision', 'post'>;
export type MatchDecisionResult = ResponseOf<'/v1/products/{productId}/listings/{accountId}/match-decision', 'post'>;
export type PriceLockReleaseResult = ResponseOf<'/v1/products/{productId}/listings/{accountId}/price-lock-release', 'post'>;
export type ChannelDescriptor = ChannelList['channels'][number];
export type CredentialField = ChannelDescriptor['fields'][number];
export type ChannelSetting = ChannelDescriptor['settings'][number];
export type ChannelAccountList = ResponseOf<'/v1/channel-accounts', 'get'>;
export type ChannelAccount = ChannelAccountList['accounts'][number];
export type ChannelCarrierList = ResponseOf<'/v1/channel-accounts/{accountId}/carriers', 'get'>;
export type ChannelSettingOptions = ResponseOf<'/v1/channel-accounts/{accountId}/settings/{key}/options', 'get'>;
export type CreateChannelAccountBody = RequestOf<'/v1/channel-accounts', 'post'>;
export type UpdateChannelAccountBody = RequestOf<'/v1/channel-accounts/{accountId}', 'patch'>;
export type ChannelCredentials = ResponseOf<'/v1/channel-accounts/{accountId}/credentials', 'get'>;
export type ChannelEvents = ResponseOf<'/v1/channel-accounts/{accountId}/events', 'get'>;
export type ChannelSyncStatus = ResponseOf<'/v1/channel-accounts/{accountId}/sync', 'get'>;
export type ChannelFeedStatus = ResponseOf<'/v1/channel-accounts/{accountId}/sync-status', 'get'>;
// Kanala Ekle: the product list's whole filter, by the list's own names, and
// where the new listings start from (ADR 0006 §1).
export type DistributeBody = RequestOf<'/v1/channel-accounts/{accountId}/distribute', 'post'>;
export type DistributeResult = ResponseOf<'/v1/channel-accounts/{accountId}/distribute', 'post'>;
export type ChannelCategorySearch = ResponseOf<'/v1/channels/{channelKey}/categories', 'get'>;
export type ChannelCategorySuggestions = ResponseOf<'/v1/channels/{channelKey}/category-suggestions', 'get'>;
export type CategoryMappingList = ResponseOf<'/v1/channels/{channelKey}/mappings', 'get'>;
export type MapCategoryBody = RequestOf<'/v1/categories/{categoryId}/mapping', 'put'>;
export type CategoryMapping = ResponseOf<'/v1/categories/{categoryId}/mapping', 'put'>;
export type ProductCategoryMapping = ResponseOf<'/v1/products/{productId}/channels/{channelKey}/category', 'put'>;
export type BulkMapProductsBody = RequestOf<'/v1/mappings/products/bulk', 'post'>;
export type BulkMapProductsResult = ResponseOf<'/v1/mappings/products/bulk', 'post'>;
export type StaleMappingList = ResponseOf<'/v1/channels/{channelKey}/mappings/stale', 'get'>;
export type StaleMapping = StaleMappingList['mappings'][number];
export type ProductImportStatus = ResponseOf<'/v1/channel-accounts/{accountId}/product-import', 'get'>;
export type ProductMatchList = ResponseOf<'/v1/product-matches', 'get'>;
export type ProductMatch = ProductMatchList['matches'][number];
export type ProductMatchCandidate = ProductMatch['candidates'][number];
export type ResolveProductMatchBody = RequestOf<'/v1/product-matches/{matchId}/resolve', 'post'>;
export type ProductSourceList = ResponseOf<'/v1/products/{productId}/sources', 'get'>;
export type ProductSource = ProductSourceList['sources'][number];
export type SettlementSummary = ResponseOf<'/v1/settlements/summary', 'get'>;
export type SettlementList = ResponseOf<'/v1/settlements', 'get'>;
export type Profitability = ResponseOf<'/v1/reports/profitability', 'get'>;
export type ProfitRow = Profitability['rows'][number];
export type ProfitGrouping = Profitability['groupBy'];
export type ProductPerformance = ResponseOf<'/v1/reports/products', 'get'>;
export type ProductPerformanceRow = ProductPerformance['rows'][number];
export type ProductPerformanceFilter = ProductPerformance['filter'];
export type DeadDays = NonNullable<NonNullable<paths['/v1/reports/products']['get']['parameters']['query']>['deadDays']>;

/** The period both settlement reads answer for, so the lines add up to the net above them. */
export interface SettlementQuery {
    /** RFC 3339 instants bounding when the marketplace dated the movement. */
    from?: string;
    to?: string;
    [key: string]: string | number | boolean | undefined;
}
/** What the profitability report covers: the orders placed in a period, one connection or all, and what a row is. */
export interface ProfitabilityQuery {
    groupBy?: ProfitGrouping;
    /** RFC 3339 instants bounding when the orders were placed. */
    from?: string;
    to?: string;
    channelAccountId?: string;
    /** Only the rows that lost money. */
    loss?: boolean;
    limit?: number;
    offset?: number;
    [key: string]: string | number | boolean | undefined;
}
/** What the product performance report covers: the period whose sales are classed, the dead-stock window, what the suggested orders plan for, and the view. */
export interface ProductPerformanceQuery {
    /** RFC 3339 instants bounding when the orders were placed. */
    from?: string;
    to?: string;
    deadDays?: DeadDays;
    /** Days an order takes to arrive and days the stock should then last, for the suggested order; the API defaults both. */
    leadDays?: number;
    coverDays?: number;
    filter?: ProductPerformanceFilter;
    limit?: number;
    offset?: number;
    [key: string]: string | number | boolean | undefined;
}
export type SalesSeries = ResponseOf<'/v1/reports/sales', 'get'>;
export type SalesPoint = SalesSeries['points'][number];
export type SalesBucket = SalesSeries['bucket'];

/** What the sales trend is read for: a window, how long a point is, and at most one connection and one product. */
export interface SalesSeriesQuery {
    bucket: SalesBucket;
    /** RFC 3339 instants bounding when the orders were placed; the API reads the last thirty days without them. */
    from?: string;
    to?: string;
    channelAccountId?: string;
    productId?: string;
    [key: string]: string | number | boolean | undefined;
}
export type ChannelWebhookInfo = ResponseOf<'/v1/channel-accounts/{accountId}/webhook', 'get'>;
export type ChannelWebhookRegistration = ResponseOf<'/v1/channel-accounts/{accountId}/webhook/register', 'post'>;
export type BulkEditBody = RequestOf<'/v1/channel-accounts/{accountId}/bulk-preview', 'post'>;
export type BulkPreview = ResponseOf<'/v1/channel-accounts/{accountId}/bulk-preview', 'post'>;
export type BulkSample = BulkPreview['samples'][number];
export type BulkApplyResult = ResponseOf<'/v1/channel-accounts/{accountId}/bulk-apply', 'post'>;

export type ProductListings = ResponseOf<'/v1/products/{productId}/listings', 'get'>;
export type ProductListing = ProductListings['listings'][number];
/** Where a listing stands among a marketplace's offers for its product (docs/04 §5.13). */
export type BuyBoxStanding = NonNullable<ProductListing['buyBox']>;
export type BuyBoxHistory = ResponseOf<'/v1/listings/{listingId}/buybox-history', 'get'>;
export type BuyBoxObservation = BuyBoxHistory['observations'][number];
export type BuyBoxHistoryQuery = NonNullable<paths['/v1/listings/{listingId}/buybox-history']['get']['parameters']['query']>;
export type PublishPlan = ResponseOf<'/v1/channel-accounts/{accountId}/publish-plan', 'get'>;
export type ChannelBatches = ResponseOf<'/v1/channel-accounts/{accountId}/batches', 'get'>;
export type ChannelBatch = ChannelBatches['batches'][number];
/** Where a submission to a channel has got to, as the API names it. */
export type BatchState = ChannelBatch['state'];
export type ChannelStockPolicies = ResponseOf<'/v1/channel-stock-policies', 'get'>;
export type ChannelStockPolicy = ResponseOf<'/v1/channel-accounts/{accountId}/stock-policy', 'put'>;
export type ChannelStockPolicyBody = RequestOf<'/v1/channel-accounts/{accountId}/stock-policy', 'put'>;
export type ChannelRows = ResponseOf<'/v1/channel-accounts/{accountId}/rows', 'get'>;
export type ChannelRow = ChannelRows['rows'][number];
export interface ChannelRowsQuery {
    search?: string;
    limit?: number;
    offset?: number;
    [key: string]: string | number | boolean | undefined;
}
export type EditChannelRowsBody = RequestOf<'/v1/channel-accounts/{accountId}/rows', 'post'>;
export type EditChannelRowsResult = ResponseOf<'/v1/channel-accounts/{accountId}/rows', 'post'>;
export type EditedRow = EditChannelRowsResult['rows'][number];
export type SetOfferBody = RequestOf<'/v1/products/{productId}/listings', 'put'>;
export type ProductChannelAttributes = ResponseOf<'/v1/products/{productId}/channels/{channelKey}/attributes', 'get'>;
export type ProductChannelAttribute = ProductChannelAttributes['attributes'][number];
export type SetProductChannelAttributesBody = RequestOf<'/v1/products/{productId}/channels/{channelKey}/attributes', 'put'>;
export type CopyProductMappingBody = RequestOf<'/v1/products/{productId}/channels/{channelKey}/copy-mapping', 'post'>;
export type CopyProductMappingResult = ResponseOf<'/v1/products/{productId}/channels/{channelKey}/copy-mapping', 'post'>;

export type OrderList = ResponseOf<'/v1/orders', 'get'>;
export type AdvanceOrdersBody = RequestOf<'/v1/orders/advance', 'post'>;
export type AdvanceOrdersResult = ResponseOf<'/v1/orders/advance', 'post'>;
export type PackageSlipsBody = RequestOf<'/v1/orders/slips', 'post'>;
export type PackageSlipsResult = ResponseOf<'/v1/orders/slips', 'post'>;
/** One printed sheet and the order it belongs to. */
export type PackageSlip = PackageSlipsResult['slips'][number];
export type OrderSummary = OrderList['orders'][number];
export type OrderDetail = ResponseOf<'/v1/orders/{orderId}', 'get'>;
export type OrderEvents = ResponseOf<'/v1/orders/{orderId}/events', 'get'>;
export type WarehouseList = ResponseOf<'/v1/warehouses', 'get'>;
export type Warehouse = WarehouseList['warehouses'][number];
export type CreateWarehouseBody = RequestOf<'/v1/warehouses', 'post'>;
export type UpdateWarehouseBody = RequestOf<'/v1/warehouses/{warehouseId}', 'put'>;
export type TransferWarehouseStockBody = RequestOf<'/v1/warehouses/{warehouseId}/transfer', 'post'>;
export type TransferWarehouseStockResult = ResponseOf<'/v1/warehouses/{warehouseId}/transfer', 'post'>;
export type UpdateSupplierBody = RequestOf<'/v1/suppliers/{supplierId}', 'patch'>;

export type ProductStock = ResponseOf<'/v1/products/{productId}/stock', 'get'>;
export type StockItem = ProductStock['items'][number];
export type LowStockList = ResponseOf<'/v1/stock/low', 'get'>;
export type StockMovements = ResponseOf<'/v1/variants/{variantId}/stock/movements', 'get'>;
export type TransferVariantStockBody = RequestOf<'/v1/variants/{variantId}/stock/transfer', 'post'>;
export type TransferVariantStockResult = ResponseOf<'/v1/variants/{variantId}/stock/transfer', 'post'>;
export type BulkStockBody = RequestOf<'/v1/stock/bulk', 'post'>;
export type BulkStockResult = ResponseOf<'/v1/stock/bulk', 'post'>;

export interface LowStockQuery {
    threshold?: number;
    /** Narrows the list to rows whose SKU or product title contains it. */
    q?: string;
    sort?: StockSort;
    limit?: number;
    offset?: number;
    [key: string]: string | number | boolean | undefined;
}

export interface OrderQuery {
    search?: string;
    status?: string;
    channelAccountId?: string;
    /** RFC 3339 instants bounding the order date. */
    from?: string;
    to?: string;
    unmatchedOnly?: boolean;
    /** 'true' for invoiced orders, 'false' for the queue that still owes a document. */
    invoiced?: string;
    sort?: OrderSort;
    limit?: number;
    offset?: number;
    [key: string]: string | number | boolean | undefined;
}

export interface QuestionQuery {
    status?: string;
    /** Where the question came from — a channel key, "storefront" or "manual". */
    source?: string;
    channelAccountId?: string;
    overdueOnly?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
    [key: string]: string | number | boolean | undefined;
}

export type DashboardSummary = ResponseOf<'/v1/dashboard', 'get'>;
export type SetupChecklist = ResponseOf<'/v1/setup', 'get'>;
export type SetupStep = SetupChecklist['steps'][number];

export type ProductImages = ResponseOf<'/v1/products/{productId}/images', 'get'>;
export type ProductImage = ProductImages['images'][number];
export type StorefrontImage = ResponseOf<'/v1/storefront/images', 'post'>;
/** What a shop picture is for, which sets the smallest the API takes. */
export type StorefrontImageUse = NonNullable<NonNullable<paths['/v1/storefront/images']['post']['parameters']['query']>['use']>;

export type SearchResults = ResponseOf<'/v1/search', 'get'>;
export type SearchResult = SearchResults['results'][number];

export type Team = ResponseOf<'/v1/team', 'get'>;
export type TeamMember = Team['members'][number];
export type TeamInvitation = Team['invitations'][number];
export type InviteBody = RequestOf<'/v1/team/invitations', 'post'>;
export type UpdateMemberBody = RequestOf<'/v1/team/{userId}', 'patch'>;
export type RoleList = ResponseOf<'/v1/roles', 'get'>;
export type TeamRole = RoleList['roles'][number];
export type SaveRoleBody = RequestOf<'/v1/roles', 'post'>;
export type PermissionList = ResponseOf<'/v1/permissions', 'get'>;
export type PermissionEntry = PermissionList['permissions'][number];
export type InvitationLookup = ResponseOf<'/v1/auth/invitation', 'get'>;
export type AcceptInvitationBody = RequestOf<'/v1/auth/invitation', 'post'>;

export type CustomerList = ResponseOf<'/v1/customers', 'get'>;
export type CustomerProfile = CustomerList['customers'][number];
export type CustomerOrders = ResponseOf<'/v1/customers/{customerId}/orders', 'get'>;
export type CustomerNotes = ResponseOf<'/v1/customers/{customerId}/notes', 'get'>;
export type UpdateCustomerBody = RequestOf<'/v1/customers/{customerId}', 'patch'>;

export type PriceRuleList = ResponseOf<'/v1/price-rules', 'get'>;
export type PriceRule = PriceRuleList['rules'][number];
export type SavePriceRuleBody = RequestOf<'/v1/price-rules', 'post'>;
/** A bulk edit kept as a pricing rule (ADR 0006 §1, layer 4), and what saving it did. */
export type StandingRuleBody = RequestOf<'/v1/price-rules/from-filter', 'post'>;
export type StandingRuleResult = ResponseOf<'/v1/price-rules/from-filter', 'post'>;
export type PricePreview = ResponseOf<'/v1/price-rules/preview', 'post'>;
export type ChannelCommissions = ResponseOf<'/v1/price-rules/commission-rates', 'post'>;
export type ReadChannelCommissionsBody = RequestOf<'/v1/price-rules/commission-rates', 'post'>;
export type PriceRunList = ResponseOf<'/v1/price-runs', 'get'>;
export type PriceRun = PriceRunList['runs'][number];
export type ExchangeRates = ResponseOf<'/v1/exchange-rates', 'get'>;

export type SupplierList = ResponseOf<'/v1/suppliers', 'get'>;
export type Supplier = SupplierList['suppliers'][number];
export type SaveSupplierBody = RequestOf<'/v1/suppliers', 'post'>;

export type FeedList = ResponseOf<'/v1/feeds', 'get'>;
export type Feed = FeedList['feeds'][number];
export type SaveFeedBody = RequestOf<'/v1/feeds', 'post'>;
export type FeedInspection = ResponseOf<'/v1/feeds/inspect', 'post'>;
export type InspectFeedBody = RequestOf<'/v1/feeds/inspect', 'post'>;
export type FeedRunList = ResponseOf<'/v1/feeds/{feedId}/runs', 'get'>;
export type FeedRun = FeedRunList['runs'][number];
export type FeedItemList = ResponseOf<'/v1/feeds/{feedId}/items', 'get'>;
// A list's products' pictures, fetched after its run, with the failures by stock code (ADR 0007).
export type FeedImages = ResponseOf<'/v1/feeds/{feedId}/images', 'get'>;
export type FeedImageProduct = FeedImages['products'][number];
export type RetriedFeedImages = ResponseOf<'/v1/feeds/{feedId}/images/retry', 'post'>;

export type ClaimList = ResponseOf<'/v1/claims', 'get'>;
export type ClaimSummary = ClaimList['claims'][number];
export type ClaimDetail = ResponseOf<'/v1/claims/{claimId}', 'get'>;
export type OrderClaims = ResponseOf<'/v1/orders/{orderId}/claims', 'get'>;
export type ClaimKind = OrderClaims['recordable'][number];
export type ClaimRejectReasons = ResponseOf<'/v1/claims/{claimId}/reject-reasons', 'get'>;
export type ReturnReasons = ResponseOf<'/v1/claims/reasons', 'get'>;
export type ReturnReason = ReturnReasons['reasons'][number];
export type ClaimRejectReason = ClaimRejectReasons['reasons'][number];
export type OpenClaimBody = RequestOf<'/v1/orders/{orderId}/claims', 'post'>;
export type DecideClaimBody = RequestOf<'/v1/claims/{claimId}/decision', 'post'>;
export type SetFulfillmentStatusBody = RequestOf<'/v1/orders/{orderId}/fulfillments/{fulfillmentId}/status', 'post'>;
export type SetFulfillmentStatusManyBody = RequestOf<'/v1/orders/{orderId}/fulfillments/status', 'post'>;
export type CancelFulfillmentBody = RequestOf<'/v1/orders/{orderId}/fulfillments/{fulfillmentId}/cancel', 'post'>;
export type SplitFulfillmentBody = RequestOf<'/v1/orders/{orderId}/fulfillments/{fulfillmentId}/split', 'post'>;
export type ChangeCarrierBody = RequestOf<'/v1/orders/{orderId}/fulfillments/{fulfillmentId}/carrier', 'post'>;
export type PackOrderLinesBody = RequestOf<'/v1/orders/{orderId}/pack', 'post'>;
export type CancelOpenLinesBody = RequestOf<'/v1/orders/{orderId}/cancel-open-lines', 'post'>;
export type LineCarriers = ResponseOf<'/v1/orders/{orderId}/lines/{lineId}/carriers', 'get'>;
export type FulfillmentCarriers = ResponseOf<'/v1/orders/{orderId}/fulfillments/{fulfillmentId}/carriers', 'get'>;
export type LineCarrier = LineCarriers['carriers'][number];
export type ChangeLineCarrierBody = RequestOf<'/v1/orders/{orderId}/lines/{lineId}/carrier', 'post'>;
export type FulfillmentLabel = ResponseOf<'/v1/orders/{orderId}/fulfillments/{fulfillmentId}/label', 'post'>;

export type CarrierList = ResponseOf<'/v1/shipping/carriers', 'get'>;
export type CarrierDescriptor = CarrierList['carriers'][number];
export type CarrierAccounts = ResponseOf<'/v1/shipping/accounts', 'get'>;
export type CarrierAccount = CarrierAccounts['accounts'][number];
export type CourierList = ResponseOf<'/v1/shipping/couriers', 'get'>;
export type Courier = CourierList['couriers'][number];
export type ChooseCouriersBody = RequestOf<'/v1/shipping/couriers', 'put'>;
export type ShippingOrigins = ResponseOf<'/v1/shipping/origins', 'get'>;
export type ShippingOrigin = ShippingOrigins['origins'][number];
export type SaveOriginBody = RequestOf<'/v1/shipping/origins', 'post'>;
export type ShipmentList = ResponseOf<'/v1/shipments', 'get'>;
export type ShipmentSummary = ShipmentList['shipments'][number];
export type OrderShipments = ResponseOf<'/v1/orders/{orderId}/shipments', 'get'>;
export type Shipment = OrderShipments['shipments'][number];
export type BookShipmentBody = RequestOf<'/v1/orders/{orderId}/shipments', 'post'>;

export type StorefrontSites = ResponseOf<'/v1/storefront/sites', 'get'>;
export type StorefrontSite = StorefrontSites['sites'][number];
export type UpdateProfileBody = RequestOf<'/v1/me', 'patch'>;
export type CompanyDetail = ResponseOf<'/v1/me/company', 'get'>;
export type UpdateCompanyBody = RequestOf<'/v1/me/company', 'patch'>;
export type ChangePasswordBody = RequestOf<'/v1/me/password', 'post'>;
export type SecondFactorBody = RequestOf<'/v1/auth/login/second-factor', 'post'>;
export type TwoFactorSetup = ResponseOf<'/v1/me/two-factor', 'post'>;
export type TwoFactorConfirmBody = RequestOf<'/v1/me/two-factor/confirm', 'post'>;
export type TwoFactorConfirmation = ResponseOf<'/v1/me/two-factor/confirm', 'post'>;
export type TwoFactorDisableBody = RequestOf<'/v1/me/two-factor/disable', 'post'>;
export type RequestEmailChangeBody = RequestOf<'/v1/me/email', 'post'>;
export type ActiveSessions = ResponseOf<'/v1/me/sessions', 'get'>;
export type ActiveSession = NonNullable<ActiveSessions['sessions']>[number];

export type SaveStorefrontSiteBody = RequestOf<'/v1/storefront/sites', 'put'>;
export type StorefrontThemes = ResponseOf<'/v1/storefront/theme', 'get'>;
export type StorefrontTheme = StorefrontThemes['live'];
export type PublishedStorefrontTheme = StorefrontThemes['history'][number];
export type StorefrontThemeBody = RequestOf<'/v1/storefront/theme/publish', 'post'>;
export type StorefrontMaintenanceBody = RequestOf<'/v1/storefront/maintenance', 'put'>;
export type StorefrontPreview = ResponseOf<'/v1/storefront/preview', 'post'>;
export type StorefrontReviews = ResponseOf<'/v1/storefront/reviews', 'get'>;
export type StorefrontReview = StorefrontReviews['reviews'][number];
export type ModerateReviewBody = RequestOf<'/v1/storefront/reviews/{reviewId}', 'put'>;
export type StorefrontReviewPhoto = StorefrontReview['photos'][number];
export type HideReviewPhotoBody = RequestOf<'/v1/storefront/reviews/{reviewId}/photos/{photoId}', 'put'>;
export type StorefrontSubscribers = ResponseOf<'/v1/storefront/subscribers', 'get'>;
export type Subscriber = StorefrontSubscribers['subscribers'][number];
export type SubscriberQuery = NonNullable<paths['/v1/storefront/subscribers']['get']['parameters']['query']>;
export type StockWaits = ResponseOf<'/v1/storefront/stock-waits', 'get'>;
export type StockWait = StockWaits['waits'][number];
export type Campaigns = ResponseOf<'/v1/newsletter/campaigns', 'get'>;
export type Campaign = Campaigns['campaigns'][number];
export type SaveCampaignBody = RequestOf<'/v1/newsletter/campaigns', 'post'>;

export type PaymentProviders = ResponseOf<'/v1/payment/providers', 'get'>;
export type PaymentProvider = PaymentProviders['providers'][number];
export type PaymentAccounts = ResponseOf<'/v1/payment/accounts', 'get'>;
export type PaymentAccount = PaymentAccounts['accounts'][number];
export type ConnectPaymentServiceBody = RequestOf<'/v1/payment/accounts', 'post'>;

export type SmsProviders = ResponseOf<'/v1/sms/providers', 'get'>;
export type SmsProvider = SmsProviders['providers'][number];
export type SmsAccounts = ResponseOf<'/v1/sms/accounts', 'get'>;
export type SmsAccount = SmsAccounts['accounts'][number];
export type ConnectSmsServiceBody = RequestOf<'/v1/sms/accounts', 'post'>;
export type UpdateSmsAccountBody = RequestOf<'/v1/sms/accounts/{accountId}', 'patch'>;
export type SendTestSmsBody = RequestOf<'/v1/sms/accounts/{accountId}/test', 'post'>;
export type SmsMessages = ResponseOf<'/v1/sms/messages', 'get'>;
export type SmsMessage = SmsMessages['messages'][number];
export type SmsMessageQuery = NonNullable<paths['/v1/sms/messages']['get']['parameters']['query']>;

export type InvoiceProviders = ResponseOf<'/v1/invoice/providers', 'get'>;
export type InvoiceProvider = InvoiceProviders['providers'][number];
export type InvoiceAccounts = ResponseOf<'/v1/invoice/accounts', 'get'>;
export type InvoiceAccount = InvoiceAccounts['accounts'][number];
export type ConnectInvoiceProviderBody = RequestOf<'/v1/invoice/accounts', 'post'>;
export type UpdateInvoiceAccountBody = RequestOf<'/v1/invoice/accounts/{accountId}', 'patch'>;
export type InvoiceBatch = ResponseOf<'/v1/invoice-batches/{batchId}', 'get'>;
export type RotateInvoiceCredentialsBody = RequestOf<'/v1/invoice/accounts/{accountId}/credentials', 'put'>;
export type InvoiceTemplates = ResponseOf<'/v1/invoice/templates', 'get'>;
export type InvoiceTemplate = InvoiceTemplates['templates'][number];
export type InvoiceTemplateBody = RequestOf<'/v1/invoice/templates', 'post'>;
export type InvoiceList = ResponseOf<'/v1/invoices', 'get'>;
export type InvoiceSummary = InvoiceList['invoices'][number];
export type InvoiceDetail = ResponseOf<'/v1/invoices/{invoiceId}', 'get'>;
export type InvoiceDocumentLink = ResponseOf<'/v1/invoices/{invoiceId}/document', 'get'>;
/** Which of an issued document's files a link opens: its rendering, or the UBL its provider filed. */
export type InvoiceDocumentFormat = NonNullable<NonNullable<paths['/v1/invoices/{invoiceId}/document']['get']['parameters']['query']>['format']>;
export type RecordInvoiceSerialBody = RequestOf<'/v1/invoices/{invoiceId}/serial', 'post'>;
export type InvoiceExemptionCodes = ResponseOf<'/v1/invoice/exemption-codes', 'get'>;
/** What the panel may offer for one document, decided by what its provider declares. */
export type InvoiceActions = InvoiceDetail['actions'];
export type InvoiceProviderSetting = InvoiceProvider['settings'][number];
export type ManualInvoiceBody = RequestOf<'/v1/invoices', 'post'>;
export type OrderInvoice = ResponseOf<'/v1/orders/{orderId}/invoice', 'get'>;
export type InvoicePreview = ResponseOf<'/v1/orders/{orderId}/invoice-preview', 'get'>;
export type IssueInvoiceBody = RequestOf<'/v1/orders/{orderId}/invoice', 'post'>;

export interface InvoiceQuery {
    status?: string;
    kind?: string;
    search?: string;
    /** RFC 3339 instants bounding when the document was created. */
    from?: string;
    to?: string;
    orderId?: string;
    limit?: number;
    offset?: number;
    [key: string]: string | number | boolean | undefined;
}

/** The invoice list's filters, which its download reads as well. */
export type InvoiceExportQuery = Pick<InvoiceQuery, 'search' | 'status' | 'kind' | 'from' | 'to'>;

/** The order list's filters, which its download reads as well; not its sort, which a file does not follow. */
export type OrderExportQuery = Pick<OrderQuery, 'search' | 'status' | 'channelAccountId' | 'from' | 'to' | 'unmatchedOnly' | 'invoiced'>;

/** The product list's filters, which its download reads as well; not its sort, which a file does not follow. */
export type ProductExportQuery = Pick<
    ProductQuery,
    'search' | 'status' | 'channelAccountId' | 'listingStatus' | 'unlisted' | 'mappingChannelKey' | 'mappingState'
>;

/**
 * Files the browser fetches itself by opening their address — a CSV export, the
 * import template. Named here, beside the calls, so no screen writes an API
 * path of its own. A list's export takes the list's own filters, so the file
 * holds what the list shows.
 */
export const downloads = {
    productsCsv: (query: ProductExportQuery) => `/v1/export/products.csv${queryString(query)}`,
    ordersCsv: (query: OrderExportQuery) => `/v1/export/orders.csv${queryString(query)}`,
    invoicesCsv: (query: InvoiceExportQuery) => `/v1/export/invoices.csv${queryString(query)}`,
    customersCsv: (query: { search?: string; tag?: string; member?: boolean }) => `/v1/export/customers.csv${queryString(query)}`,
    claimsCsv: (query: { status?: string }) => `/v1/export/claims.csv${queryString(query)}`,
    /** The workbook (ADR 0019 §6); the CSV template stays at its old address for links already shared. */
    productImportTemplate: '/v1/import/products/template.xlsx',
    subscribersCsv: '/v1/export/subscribers.csv',
    /** A sales channel's prices and stock as the workbook its listing sheet is edited in (ADR 0006 §1). */
    listingSheet: (accountId: string) => `/v1/export/listings.xlsx${queryString({ accountId })}`,
    /** Every list the session may read, one CSV file each, in a ZIP. */
    everything: '/v1/export/all.zip',
} as const;

export const endpoints = {
    appConfig: () => api.get<AppConfig>('/v1/app-config'),
    me: () => api.get<Me>('/v1/me'),
    login: (body: LoginBody) => api.post<SessionPayload>('/v1/auth/login', body),
    register: (body: RegisterBody) => api.post<SessionPayload>('/v1/auth/register', body),
    logout: () => api.post<{ ok: boolean }>('/v1/auth/logout'),
    selectCompany: (companyId: string) => api.post<{ ok: boolean }>('/v1/me/company', { companyId }),
    acceptTerms: (body: AcceptTermsBody) => api.post<void>('/v1/me/terms-acceptance', body),

    subscription: () => api.get<Subscription>('/v1/billing/subscription'),
    cancelSubscription: () => api.post<Subscription>('/v1/billing/subscription/cancel'),
    resumeSubscription: () => api.post<void>('/v1/billing/subscription/resume'),
    plans: () => api.get<PlanList>('/v1/billing/plans'),
    paymentOptions: () => api.get<PaymentOptions>('/v1/billing/payment-options'),
    purchasePlan: (body: PurchaseBody) => api.post<Purchase>('/v1/billing/purchase', body),
    billingCharges: () => api.get<BillingCharges>('/v1/billing/charges'),
    cancelBillingCharge: (id: string) => api.post<void>(`/v1/billing/charges/${id}/cancel`),
    billingChargeInvoiceLink: (id: string) => api.get<BillingChargeInvoiceLink>(`/v1/billing/charges/${id}/invoice`),
    billingAgreement: (query: BillingAgreementQuery) => api.get<BillingAgreement>(`/v1/billing/agreement${queryString(query)}`),
    billingChargeAgreement: (id: string) => api.get<BillingChargeAgreement>(`/v1/billing/charges/${id}/agreement`),
    publicPlans: () => api.get<PublicPlanList>('/v1/public/plans'),
    // Until card billing is connected, a plan change is a request the platform
    // staff act on; it is recorded against the account, not sent as an e-mail.
    requestPlanChange: (body: PlanRequestBody) => api.post<{ id: string }>('/v1/contact/plan-request', body),
    contactSupport: (body: SupportRequestBody) => api.post<{ id: string }>('/v1/contact/support', body),

    importProducts: (file: File, commit: boolean) => {
        const form = new FormData();
        form.append('file', file);
        return api.post<ProductImportResult>(`/v1/import/products?commit=${commit}`, form);
    },
    importListingSheet: (accountId: string, file: File, commit: boolean) => {
        const form = new FormData();
        form.append('file', file);
        return api.post<ListingSheetResult>(`/v1/import/listings${queryString({ accountId, commit })}`, form);
    },

    bulkSetProductStatus: (productIds: string[], status: string) => api.post<BulkEdit>('/v1/products/bulk-status', { productIds, status }),
    bulkDeleteProducts: (body: BulkDeleteProductsBody) => api.post<{ deleted: number }>('/v1/products/bulk-delete', body),
    // An omitted categoryId clears the filing, which is how a mistaken run is
    // corrected forwards as well as reverted.
    bulkSetProductCategory: (productIds: string[], categoryId?: string) =>
        api.post<BulkEdit>('/v1/products/bulk-category', { productIds, categoryId }),
    revertBulkEdit: (id: string) => api.post<{ restored: number }>(`/v1/products/bulk-edits/${id}/revert`),
    advanceOrders: (body: AdvanceOrdersBody) => api.post<AdvanceOrdersResult>('/v1/orders/advance', body),
    packageSlips: (body: PackageSlipsBody) => api.post<PackageSlipsResult>('/v1/orders/slips', body),

    bulkRematchOrders: (orderIds: string[]) =>
        api.post<{ orders: number; resolved: number; failed: number }>('/v1/orders/bulk-rematch', { orderIds }),
    startInvoiceBatch: (orderIds: string[]) => api.post<InvoiceBatch>('/v1/invoice-batches', { orderIds }),
    invoiceBatch: (id: string) => api.get<InvoiceBatch>(`/v1/invoice-batches/${id}`),

    questions: (query: QuestionQuery = {}) => api.get<QuestionList>(`/v1/questions${queryString(query)}`),
    question: (id: string) => api.get<QuestionDetail>(`/v1/questions/${id}`),
    answerQuestion: (id: string, answer: string) => api.post<Question>(`/v1/questions/${id}/answer`, { answer }),
    cannedAnswers: () => api.get<CannedList>('/v1/canned-answers'),
    anonymiseCustomer: (id: string) => api.post<Record<string, never>>(`/v1/customers/${id}/anonymise`),

    coupons: () => api.get<CouponList>('/v1/storefront/coupons'),
    saveCoupon: (body: SaveCouponBody) => api.put<Coupon>('/v1/storefront/coupons', body),
    deleteCoupon: (id: string) => api.delete<void>(`/v1/storefront/coupons/${id}`),

    promotions: () => api.get<PromotionList>('/v1/storefront/promotions'),
    createPromotion: (body: SavePromotionBody) => api.post<Promotion>('/v1/storefront/promotions', body),
    updatePromotion: (id: string, body: SavePromotionBody) => api.put<Promotion>(`/v1/storefront/promotions/${id}`, body),
    endPromotion: (id: string) => api.post<Promotion>(`/v1/storefront/promotions/${id}/end`),
    sizeGuides: () => api.get<SizeGuideList>('/v1/storefront/size-guides'),
    createSizeGuide: (body: SaveSizeGuideBody) => api.post<SizeGuide>('/v1/storefront/size-guides', body),
    updateSizeGuide: (id: string, body: SaveSizeGuideBody) => api.put<SizeGuide>(`/v1/storefront/size-guides/${id}`, body),
    deleteSizeGuide: (id: string) => api.delete<void>(`/v1/storefront/size-guides/${id}`),

    notifications: () => api.get<NotificationList>('/v1/notifications'),
    markNotificationRead: (id: string) => api.post<{ ok: boolean }>(`/v1/notifications/${id}/read`),
    markAllNotificationsRead: () => api.post<{ ok: boolean }>('/v1/notifications/read-all'),
    notificationPreferences: () => api.get<NotificationPreferences>('/v1/me/notification-preferences'),
    saveNotificationPreferences: (body: SaveNotificationPreferencesBody) => api.put<NotificationPreferences>('/v1/me/notification-preferences', body),

    apiTokens: () => api.get<ApiTokenList>('/v1/api-tokens'),
    createApiToken: (name: string, scopes: string[]) => api.post<CreatedApiToken>('/v1/api-tokens', { name, scopes }),
    revokeApiToken: (id: string) => api.delete<Record<string, never>>(`/v1/api-tokens/${id}`),

    webhooks: () => api.get<WebhookList>('/v1/webhooks'),
    createWebhook: (url: string, secret: string, events: string[]) => api.post<Webhook>('/v1/webhooks', { url, secret, events }),
    deleteWebhook: (id: string) => api.delete<Record<string, never>>(`/v1/webhooks/${id}`),
    webhookDeliveries: () => api.get<WebhookDeliveryList>('/v1/webhooks/deliveries'),
    testWebhook: () => api.post<Record<string, never>>('/v1/webhooks/test'),

    listProducts: (query: ProductQuery = {}) => api.get<ProductList>(`/v1/products${queryString(query)}`),
    getProduct: (id: string) => api.get<Product>(`/v1/products/${id}`),
    createProduct: (body: CreateProductBody) => api.post<Product>('/v1/products', body),
    updateProduct: (productId: string, body: UpdateProductBody) => api.put<Product>(`/v1/products/${productId}`, body),
    listBrands: () => api.get<BrandList>('/v1/brands'),
    createBrand: (body: CreateBrandBody) => api.post<Brand>('/v1/brands', body),
    brandOverview: (query: { q?: string; limit?: number; offset?: number }) => api.get<BrandOverview>(`/v1/brand-overview${queryString(query)}`),
    updateBrand: (brandId: string, body: UpdateBrandBody) => api.patch<Brand>(`/v1/brands/${brandId}`, body),
    mergeBrand: (brandId: string, intoBrandId: string) => api.post<BrandMergeResult>(`/v1/brands/${brandId}/merge`, { intoBrandId }),
    deleteBrand: (brandId: string) => api.delete<void>(`/v1/brands/${brandId}`),
    uploadBrandLogo: (file: File) => api.post<BrandLogo>('/v1/brand-logos', file, { headers: { 'Content-Type': file.type } }),
    updateProductVariants: (productId: string, body: UpdateProductVariantsBody) => api.patch<Product>(`/v1/products/${productId}/variants`, body),
    addProductVariant: (productId: string, body: AddProductVariantBody) => api.post<Product>(`/v1/products/${productId}/variants`, body),
    duplicateProduct: (productId: string, body: DuplicateProductBody) => api.post<Product>(`/v1/products/${productId}/duplicate`, body),
    listDescriptionTemplates: () => api.get<DescriptionTemplateList>('/v1/description-templates'),
    createDescriptionTemplate: (body: CreateDescriptionTemplateBody) => api.post<DescriptionTemplate>('/v1/description-templates', body),
    deleteDescriptionTemplate: (id: string) => api.delete<void>(`/v1/description-templates/${id}`),
    setProductPrices: (productId: string, body: SetProductPricesBody) => api.put<Product>(`/v1/products/${productId}/prices`, body),
    publishProduct: (id: string, body: PublishProductBody) => api.post<{ ok: boolean }>(`/v1/products/${id}/publish`, body),
    archiveProduct: (id: string) => api.post<{ ok: boolean }>(`/v1/products/${id}/archive`, {}),
    deleteProduct: (id: string) => api.delete<void>(`/v1/products/${id}`),

    productMatches: (query: { limit?: number; offset?: number } = {}) => api.get<ProductMatchList>(`/v1/product-matches${queryString(query)}`),
    resolveProductMatch: (matchId: string, body: ResolveProductMatchBody) =>
        api.post<{ ok: boolean }>(`/v1/product-matches/${matchId}/resolve`, body),
    productSources: (productId: string) => api.get<ProductSourceList>(`/v1/products/${productId}/sources`),
    unlinkProductSource: (productId: string, sourceId: string) => api.delete<{ ok: boolean }>(`/v1/products/${productId}/sources/${sourceId}`),

    renameCategory: (id: string, body: RenameCategoryBody) => api.patch<void>(`/v1/categories/${id}`, body),
    listCategories: () => api.get<CategoryList>('/v1/categories'),

    settlementSummary: (query: SettlementQuery = {}) => api.get<SettlementSummary>(`/v1/settlements/summary${queryString(query)}`),
    settlements: (query: SettlementQuery = {}) => api.get<SettlementList>(`/v1/settlements${queryString(query)}`),
    profitability: (query: ProfitabilityQuery = {}) => api.get<Profitability>(`/v1/reports/profitability${queryString(query)}`),
    productPerformance: (query: ProductPerformanceQuery = {}) => api.get<ProductPerformance>(`/v1/reports/products${queryString(query)}`),
    salesSeries: (query: SalesSeriesQuery) => api.get<SalesSeries>(`/v1/reports/sales${queryString(query)}`),

    listCategoryMappings: (channelKey: string) => api.get<CategoryMappingList>(`/v1/channels/${channelKey}/mappings`),
    searchChannelCategories: (channelKey: string, search: string, limit = 50) =>
        api.get<ChannelCategorySearch>(`/v1/channels/${channelKey}/categories${queryString({ search, limit })}`),
    suggestChannelCategories: (channelKey: string, text: string) =>
        api.get<ChannelCategorySuggestions>(`/v1/channels/${channelKey}/category-suggestions${queryString({ text })}`),
    mapCategory: (categoryId: string, body: MapCategoryBody) => api.put<CategoryMapping>(`/v1/categories/${categoryId}/mapping`, body),
    unmapCategory: (categoryId: string, channelKey: string) => api.delete<{ ok: boolean }>(`/v1/categories/${categoryId}/mapping/${channelKey}`),
    mapProductCategory: (productId: string, channelKey: string, channelCategoryId: string) =>
        api.put<ProductCategoryMapping>(`/v1/products/${productId}/channels/${channelKey}/category`, { channelKey, channelCategoryId }),
    unmapProductCategory: (productId: string, channelKey: string) =>
        api.delete<{ ok: boolean }>(`/v1/products/${productId}/channels/${channelKey}/category`),
    bulkMapProducts: (body: BulkMapProductsBody) => api.post<BulkMapProductsResult>('/v1/mappings/products/bulk', body),
    staleCategoryMappings: (channelKey: string) => api.get<StaleMappingList>(`/v1/channels/${channelKey}/mappings/stale`),
    productChannelAttributes: (productId: string, channelKey: string) =>
        api.get<ProductChannelAttributes>(`/v1/products/${productId}/channels/${channelKey}/attributes`),
    copyProductMapping: (productId: string, channelKey: string, body: CopyProductMappingBody) =>
        api.post<CopyProductMappingResult>(`/v1/products/${productId}/channels/${channelKey}/copy-mapping`, body),
    setProductChannelAttributes: (productId: string, channelKey: string, body: SetProductChannelAttributesBody) =>
        api.put<ProductChannelAttributes>(`/v1/products/${productId}/channels/${channelKey}/attributes`, body),

    searchChannelBrands: (channelKey: string, q: string, categoryExternalId?: string) =>
        api.get<ChannelBrandSearch>(`/v1/channels/${channelKey}/brands${queryString({ q, categoryExternalId })}`),
    suggestChannelBrands: (channelKey: string, text: string) =>
        api.get<ChannelBrandSuggestions>(`/v1/channels/${channelKey}/brand-suggestions${queryString({ text })}`),
    searchBrands: (q: string) => api.get<BrandSearch>(`/v1/brand-search${queryString({ q })}`),
    listBrandMappings: (channelKey: string, query: { state?: BrandMappingState; limit?: number; offset?: number } = {}) =>
        api.get<BrandMappingList>(`/v1/channels/${channelKey}/brand-mappings${queryString(query)}`),
    mapBrand: (brandId: string, channelKey: string, body: MapBrandBody) => api.put<BrandMapping>(`/v1/brands/${brandId}/mapping/${channelKey}`, body),
    unmapBrand: (brandId: string, channelKey: string) => api.delete<{ ok: boolean }>(`/v1/brands/${brandId}/mapping/${channelKey}`),
    acceptBrandSuggestions: (channelKey: string, body: AcceptBrandSuggestionsBody) =>
        api.post<AcceptBrandSuggestionsResult>(`/v1/channels/${channelKey}/brand-mappings/accept`, body),
    productChannelBrand: (productId: string, channelKey: string) =>
        api.get<ProductChannelBrand>(`/v1/products/${productId}/channels/${channelKey}/brand`),
    setProductChannelBrand: (productId: string, channelKey: string, body: SetProductChannelBrandBody) =>
        api.put<ProductChannelBrand>(`/v1/products/${productId}/channels/${channelKey}/brand`, body),
    clearProductChannelBrand: (productId: string, channelKey: string) =>
        api.delete<{ ok: boolean }>(`/v1/products/${productId}/channels/${channelKey}/brand`),
    requestChannelBrand: (accountId: string, body: BrandRequestBody) =>
        api.post<RequestedBrand>(`/v1/channel-accounts/${accountId}/brand-requests`, body),

    listChannels: () => api.get<ChannelList>('/v1/channels'),
    listChannelAccounts: () => api.get<ChannelAccountList>('/v1/channel-accounts'),
    createChannelAccount: (body: CreateChannelAccountBody) => api.post<ChannelAccount>('/v1/channel-accounts', body),
    updateChannelAccount: (id: string, body: UpdateChannelAccountBody) => api.patch<ChannelAccount>(`/v1/channel-accounts/${id}`, body),
    channelCarriers: (id: string) => api.get<ChannelCarrierList>(`/v1/channel-accounts/${id}/carriers`),
    channelAccountCredentials: (id: string) => api.get<ChannelCredentials>(`/v1/channel-accounts/${id}/credentials`),
    channelAccountEvents: (id: string) => api.get<ChannelEvents>(`/v1/channel-accounts/${id}/events`),
    verifyChannelAccount: (id: string) => api.post<ChannelAccount>(`/v1/channel-accounts/${id}/verify`),
    deleteChannelAccount: (id: string) => api.delete<{ ok: boolean }>(`/v1/channel-accounts/${id}`),
    channelSyncStatus: (id: string) => api.get<ChannelSyncStatus>(`/v1/channel-accounts/${id}/sync`),
    channelFeedStatus: (id: string) => api.get<ChannelFeedStatus>(`/v1/channel-accounts/${id}/sync-status`),
    distributeToChannel: (targetId: string, body: DistributeBody) => api.post<DistributeResult>(`/v1/channel-accounts/${targetId}/distribute`, body),
    syncChannelNow: (id: string) => api.post<ChannelSyncStatus>(`/v1/channel-accounts/${id}/sync`),
    productImportStatus: (id: string) => api.get<ProductImportStatus>(`/v1/channel-accounts/${id}/product-import`),
    importProductsNow: (id: string) => api.post<ProductImportStatus>(`/v1/channel-accounts/${id}/product-import`),
    channelWebhook: (id: string) => api.get<ChannelWebhookInfo>(`/v1/channel-accounts/${id}/webhook`),
    registerChannelWebhook: (id: string) => api.post<ChannelWebhookRegistration>(`/v1/channel-accounts/${id}/webhook/register`),
    unregisterChannelWebhook: (id: string) => api.delete<{ ok: boolean }>(`/v1/channel-accounts/${id}/webhook/register`),
    bulkPreview: (accountId: string, body: BulkEditBody) => api.post<BulkPreview>(`/v1/channel-accounts/${accountId}/bulk-preview`, body),
    bulkApply: (accountId: string, body: BulkEditBody) => api.post<BulkApplyResult>(`/v1/channel-accounts/${accountId}/bulk-apply`, body),

    productListings: (productId: string) => api.get<ProductListings>(`/v1/products/${productId}/listings`),
    listingBuyBoxHistory: (listingId: string, query: BuyBoxHistoryQuery = {}) =>
        api.get<BuyBoxHistory>(`/v1/listings/${listingId}/buybox-history${queryString(query)}`),
    setOffer: (productId: string, body: SetOfferBody) => api.put<ProductListings>(`/v1/products/${productId}/listings`, body),
    channelPublishPlan: (accountId: string) => api.get<PublishPlan>(`/v1/channel-accounts/${accountId}/publish-plan`),
    channelBatches: (accountId: string) => api.get<ChannelBatches>(`/v1/channel-accounts/${accountId}/batches`),
    channelStockPolicies: () => api.get<ChannelStockPolicies>('/v1/channel-stock-policies'),
    setChannelStockPolicy: (accountId: string, body: ChannelStockPolicyBody) =>
        api.put<ChannelStockPolicy>(`/v1/channel-accounts/${accountId}/stock-policy`, body),
    channelRows: (accountId: string, query: ChannelRowsQuery = {}) =>
        api.get<ChannelRows>(`/v1/channel-accounts/${accountId}/rows${queryString(query)}`),
    editChannelRows: (accountId: string, body: EditChannelRowsBody) =>
        api.post<EditChannelRowsResult>(`/v1/channel-accounts/${accountId}/rows`, body),
    publishChannel: (accountId: string, productId?: string) =>
        api.post<PublishResult>(`/v1/channel-accounts/${accountId}/publish${queryString({ productId })}`),
    publishChannelSelection: (accountId: string, body: PublishSelectionBody) =>
        api.post<PublishSelectionResult>(`/v1/channel-accounts/${accountId}/publish-selection`, body),
    withdrawFromChannel: (productId: string, accountId: string) =>
        api.post<WithdrawResult>(`/v1/products/${productId}/listings/${accountId}/withdraw`),
    decideChannelMatch: (productId: string, accountId: string, body: MatchDecisionBody) =>
        api.post<MatchDecisionResult>(`/v1/products/${productId}/listings/${accountId}/match-decision`, body),
    releaseChannelPriceLock: (productId: string, accountId: string) =>
        api.post<PriceLockReleaseResult>(`/v1/products/${productId}/listings/${accountId}/price-lock-release`),

    listOrders: (query: OrderQuery = {}) => api.get<OrderList>(`/v1/orders${queryString(query)}`),
    getOrder: (id: string) => api.get<OrderDetail>(`/v1/orders/${id}`),
    orderEvents: (id: string) => api.get<OrderEvents>(`/v1/orders/${id}/events`),
    rematchOrder: (id: string) => api.post<{ resolved: number }>(`/v1/orders/${id}/rematch`),
    receiveOrderPayment: (id: string) => api.post<void>(`/v1/orders/${id}/payment-received`),
    setFulfillmentStatus: (orderId: string, fulfillmentId: string, body: SetFulfillmentStatusBody) =>
        api.post<ResponseOf<'/v1/orders/{orderId}/fulfillments/{fulfillmentId}/status', 'post'>>(
            `/v1/orders/${orderId}/fulfillments/${fulfillmentId}/status`,
            body,
        ),
    setFulfillmentStatusMany: (orderId: string, body: SetFulfillmentStatusManyBody) =>
        api.post<{ queued: boolean }>(`/v1/orders/${orderId}/fulfillments/status`, body),
    cancelFulfillmentItems: (orderId: string, fulfillmentId: string, body: CancelFulfillmentBody) =>
        api.post<{ queued: boolean }>(`/v1/orders/${orderId}/fulfillments/${fulfillmentId}/cancel`, body),
    splitFulfillment: (orderId: string, fulfillmentId: string, body: SplitFulfillmentBody) =>
        api.post<{ queued: boolean }>(`/v1/orders/${orderId}/fulfillments/${fulfillmentId}/split`, body),
    changeFulfillmentCarrier: (orderId: string, fulfillmentId: string, body: ChangeCarrierBody) =>
        api.post<{ queued: boolean }>(`/v1/orders/${orderId}/fulfillments/${fulfillmentId}/carrier`, body),
    packOrderLines: (orderId: string, body: PackOrderLinesBody) => api.post<{ queued: boolean }>(`/v1/orders/${orderId}/pack`, body),
    cancelOpenLines: (orderId: string, body: CancelOpenLinesBody) => api.post<{ queued: boolean }>(`/v1/orders/${orderId}/cancel-open-lines`, body),
    lineCarriers: (orderId: string, lineId: string) => api.get<LineCarriers>(`/v1/orders/${orderId}/lines/${lineId}/carriers`),
    fulfillmentCarriers: (orderId: string, fulfillmentId: string) =>
        api.get<FulfillmentCarriers>(`/v1/orders/${orderId}/fulfillments/${fulfillmentId}/carriers`),
    changeLineCarrier: (orderId: string, lineId: string, body: ChangeLineCarrierBody) =>
        api.post<{ queued: boolean }>(`/v1/orders/${orderId}/lines/${lineId}/carrier`, body),
    fulfillmentLabel: (orderId: string, fulfillmentId: string, format?: string) =>
        api.post<FulfillmentLabel>(`/v1/orders/${orderId}/fulfillments/${fulfillmentId}/label${queryString({ format })}`),
    fulfillmentSlip: (orderId: string, fulfillmentId: string, options: { products?: boolean; prices?: boolean }) =>
        api.post<FulfillmentLabel>(
            `/v1/orders/${orderId}/fulfillments/${fulfillmentId}/slip${queryString({ products: options.products, prices: options.prices })}`,
        ),

    channelSettingOptions: (accountId: string, key: string) =>
        api.get<ChannelSettingOptions>(`/v1/channel-accounts/${accountId}/settings/${key}/options`),

    warehouses: () => api.get<WarehouseList>('/v1/warehouses'),
    updateWarehouse: (id: string, body: UpdateWarehouseBody) => api.put<Warehouse>(`/v1/warehouses/${id}`, body),
    createWarehouse: (body: CreateWarehouseBody) => api.post<Warehouse>('/v1/warehouses', body),
    setDefaultWarehouse: (id: string) => api.post<{ ok: boolean }>(`/v1/warehouses/${id}/default`),
    deleteWarehouse: (id: string) => api.delete<{ ok: boolean }>(`/v1/warehouses/${id}`),
    transferWarehouseStock: (id: string, body: TransferWarehouseStockBody) =>
        api.post<TransferWarehouseStockResult>(`/v1/warehouses/${id}/transfer`, body),

    productStock: (productId: string) => api.get<ProductStock>(`/v1/products/${productId}/stock`),
    adjustStock: (
        variantId: string,
        body: {
            delta?: number;
            setTo?: number;
            note?: string;
            warehouseId?: string;
        },
    ) => api.post<StockItem>(`/v1/variants/${variantId}/stock`, body),
    transferVariantStock: (variantId: string, body: TransferVariantStockBody) =>
        api.post<TransferVariantStockResult>(`/v1/variants/${variantId}/stock/transfer`, body),
    stockMovements: (variantId: string) => api.get<StockMovements>(`/v1/variants/${variantId}/stock/movements`),
    team: () => api.get<Team>('/v1/team'),
    invite: (body: InviteBody) => api.post<TeamInvitation>('/v1/team/invitations', body),
    revokeInvitation: (id: string) => api.delete<{ ok: boolean }>(`/v1/team/invitations/${id}`),
    updateMember: (userId: string, body: UpdateMemberBody) => api.patch<TeamMember>(`/v1/team/${userId}`, body),

    roles: () => api.get<RoleList>('/v1/roles'),
    permissions: () => api.get<PermissionList>('/v1/permissions'),
    createRole: (body: SaveRoleBody) => api.post<TeamRole>('/v1/roles', body),
    updateRole: (id: string, body: SaveRoleBody) => api.put<TeamRole>(`/v1/roles/${id}`, body),
    deleteRole: (id: string) => api.delete<{ ok: boolean }>(`/v1/roles/${id}`),

    // Unauthenticated: whoever holds the link has no session yet, which is the
    // whole reason the link exists.
    invitation: (token: string) => api.get<InvitationLookup>(`/v1/auth/invitation?token=${encodeURIComponent(token)}`),
    acceptInvitation: (body: AcceptInvitationBody) => api.post<SessionPayload>('/v1/auth/invitation', body),
    verifyEmail: (token: string) => api.post<{ ok: boolean }>('/v1/auth/verify-email', { token }),
    resendEmailVerification: () => api.post<{ ok: boolean }>('/v1/auth/verify-email/resend', {}),

    company: () => api.get<CompanyDetail>('/v1/me/company'),
    updateCompany: (body: UpdateCompanyBody) => api.patch<{ ok: boolean }>('/v1/me/company', body),
    updateProfile: (body: UpdateProfileBody) => api.patch<{ ok: boolean }>('/v1/me', body),
    changePassword: (body: ChangePasswordBody) => api.post<{ ok: boolean }>('/v1/me/password', body),
    requestEmailChange: (body: RequestEmailChangeBody) => api.post<{ ok: boolean }>('/v1/me/email', body),
    confirmEmailChange: (token: string) => api.post<{ ok: boolean }>('/v1/auth/email-change/confirm', { token }),
    forgotPassword: (email: string) => api.post<{ ok: boolean }>('/v1/auth/forgot-password', { email }),
    resetPassword: (token: string, password: string) => api.post<{ ok: boolean }>('/v1/auth/reset-password', { token, password }),
    sessions: () => api.get<ActiveSessions>('/v1/me/sessions'),
    revokeSession: (id: string) => api.delete<void>(`/v1/me/sessions/${id}`),
    endOtherSessions: () => api.post<{ ok: boolean }>('/v1/me/sessions/end-others'),
    // Two-step sign-in (docs/07 §4): the code that finishes a sign-in is
    // unauthenticated; setting it up and turning it off are the person's own.
    completeSecondFactor: (body: SecondFactorBody) => api.post<SessionPayload>('/v1/auth/login/second-factor', body),
    beginTwoFactor: () => api.post<TwoFactorSetup>('/v1/me/two-factor'),
    confirmTwoFactor: (body: TwoFactorConfirmBody) => api.post<TwoFactorConfirmation>('/v1/me/two-factor/confirm', body),
    disableTwoFactor: (body: TwoFactorDisableBody) => api.post<{ ok: boolean }>('/v1/me/two-factor/disable', body),

    customers: (
        query: {
            search?: string;
            tag?: string;
            member?: boolean;
            sort?: CustomerSort;
            limit?: number;
            offset?: number;
        } = {},
    ) => api.get<CustomerList>(`/v1/customers${queryString(query)}`),
    customer: (id: string) => api.get<CustomerProfile>(`/v1/customers/${id}`),
    customerOrders: (id: string) => api.get<CustomerOrders>(`/v1/customers/${id}/orders`),
    customerNotes: (id: string) => api.get<CustomerNotes>(`/v1/customers/${id}/notes`),
    addCustomerNote: (id: string, body: string) => api.post<{ id: string }>(`/v1/customers/${id}/notes`, { body }),
    updateCustomer: (id: string, body: UpdateCustomerBody) => api.patch<CustomerProfile>(`/v1/customers/${id}`, body),

    priceRules: () => api.get<PriceRuleList>('/v1/price-rules'),
    updatePriceRule: (id: string, body: SavePriceRuleBody) => api.put<PriceRule>(`/v1/price-rules/${id}`, body),
    createPriceRule: (body: SavePriceRuleBody) => api.post<PriceRule>('/v1/price-rules', body),
    createStandingRule: (body: StandingRuleBody) => api.post<StandingRuleResult>('/v1/price-rules/from-filter', body),
    deletePriceRule: (id: string) => api.delete<void>(`/v1/price-rules/${id}`),
    previewPrices: (ruleId?: string) => api.post<PricePreview>('/v1/price-rules/preview', ruleId ? { ruleId } : {}),
    readChannelCommissions: (body: ReadChannelCommissionsBody) => api.post<ChannelCommissions>('/v1/price-rules/commission-rates', body),
    applyPrices: (ruleId?: string) => api.post<PriceRun>('/v1/price-rules/apply', ruleId ? { ruleId } : {}),
    priceRuns: (limit = 20) => api.get<PriceRunList>(`/v1/price-runs?limit=${limit}`),
    revertPriceRun: (id: string) => api.post<{ restored: number }>(`/v1/price-runs/${id}/revert`),
    exchangeRates: () => api.get<ExchangeRates>('/v1/exchange-rates'),

    suppliers: () => api.get<SupplierList>('/v1/suppliers'),
    updateSupplier: (id: string, body: UpdateSupplierBody) => api.patch<Supplier>(`/v1/suppliers/${id}`, body),
    createSupplier: (body: SaveSupplierBody) => api.post<Supplier>('/v1/suppliers', body),

    feeds: () => api.get<FeedList>('/v1/feeds'),
    feed: (id: string) => api.get<Feed>(`/v1/feeds/${id}`),
    createFeed: (body: SaveFeedBody) => api.post<Feed>('/v1/feeds', body),
    updateFeed: (id: string, body: SaveFeedBody) => api.put<Feed>(`/v1/feeds/${id}`, body),
    deleteFeed: (id: string) => api.delete<{ ok: boolean }>(`/v1/feeds/${id}`),
    // Reads the head of a file nobody has saved yet, which is what lets the
    // mapping screen show real values beside every path.
    inspectFeedURL: (body: InspectFeedBody) => api.post<FeedInspection>('/v1/feeds/inspect', body),
    startFeedRun: (id: string, mode: 'preview' | 'apply' | 'resync') => api.post<FeedRun>(`/v1/feeds/${id}/runs`, { mode }),
    feedRuns: (id: string) => api.get<FeedRunList>(`/v1/feeds/${id}/runs`),
    feedItems: (
        id: string,
        query: {
            status?: string;
            search?: string;
            limit?: number;
            offset?: number;
        } = {},
    ) => api.get<FeedItemList>(`/v1/feeds/${id}/items${queryString(query)}`),
    feedImages: (id: string, query: { limit?: number; offset?: number } = {}) => api.get<FeedImages>(`/v1/feeds/${id}/images${queryString(query)}`),
    retryFeedImages: (id: string) => api.post<RetriedFeedImages>(`/v1/feeds/${id}/images/retry`),

    claims: (
        query: {
            status?: string;
            openOnly?: boolean;
            sort?: ClaimSort;
            limit?: number;
            offset?: number;
        } = {},
    ) => api.get<ClaimList>(`/v1/claims${queryString(query)}`),
    claim: (id: string) => api.get<ClaimDetail>(`/v1/claims/${id}`),
    orderClaims: (orderId: string) => api.get<OrderClaims>(`/v1/orders/${orderId}/claims`),
    openClaim: (orderId: string, body: OpenClaimBody) => api.post<ClaimDetail>(`/v1/orders/${orderId}/claims`, body),
    decideClaim: (id: string, body: DecideClaimBody) => api.post<ClaimDetail>(`/v1/claims/${id}/decision`, body),
    claimRejectReasons: (id: string) => api.get<ClaimRejectReasons>(`/v1/claims/${id}/reject-reasons`),
    preApproveClaim: (id: string) => api.post<ClaimDetail>(`/v1/claims/${id}/pre-approval`),
    returnReasons: (days: number) => api.get<ReturnReasons>(`/v1/claims/reasons?days=${days}`),

    carriers: () => api.get<CarrierList>('/v1/shipping/carriers'),
    carrierAccounts: () => api.get<CarrierAccounts>('/v1/shipping/accounts'),
    couriers: () => api.get<CourierList>('/v1/shipping/couriers'),
    chooseCouriers: (body: ChooseCouriersBody) => api.put<CourierList>('/v1/shipping/couriers', body),

    shippingOrigins: () => api.get<ShippingOrigins>('/v1/shipping/origins'),
    deleteShippingOrigin: (id: string) => api.delete<void>(`/v1/shipping/origins/${id}`),
    saveShippingOrigin: (body: SaveOriginBody) => api.post<ShippingOrigin>('/v1/shipping/origins', body),

    shipments: (
        query: {
            status?: string;
            openOnly?: boolean;
            limit?: number;
            offset?: number;
        } = {},
    ) => api.get<ShipmentList>(`/v1/shipments${queryString(query)}`),
    orderShipments: (orderId: string) => api.get<OrderShipments>(`/v1/orders/${orderId}/shipments`),
    bookShipment: (orderId: string, body: BookShipmentBody) => api.post<Shipment>(`/v1/orders/${orderId}/shipments`, body),
    shipmentLabel: (id: string) => api.get<{ url: string; format: string }>(`/v1/shipments/${id}/label`),
    cancelShipment: (id: string, reason: string) => api.post<Shipment>(`/v1/shipments/${id}/cancel`, { reason }),

    storefrontSites: () => api.get<StorefrontSites>('/v1/storefront/sites'),
    uploadStorefrontImage: (file: File, use: StorefrontImageUse) =>
        api.post<StorefrontImage>(`/v1/storefront/images${queryString({ use })}`, file, {
            headers: { 'Content-Type': file.type },
        }),
    saveStorefrontSite: (body: SaveStorefrontSiteBody) => api.put<StorefrontSite>('/v1/storefront/sites', body),
    setStorefrontPublished: (published: boolean) => api.put<void>('/v1/storefront/published', { published }),
    setStorefrontMaintenance: (body: StorefrontMaintenanceBody) => api.put<void>('/v1/storefront/maintenance', body),
    issueStorefrontPreview: () => api.post<StorefrontPreview>('/v1/storefront/preview'),
    deleteStorefrontSite: () => api.delete<void>('/v1/storefront/sites'),
    verifyStorefrontDomain: () => api.post<StorefrontSite>('/v1/storefront/domain-verification'),
    storefrontTheme: () => api.get<StorefrontThemes>('/v1/storefront/theme'),
    saveStorefrontThemeDraft: (body: StorefrontThemeBody) => api.put<StorefrontThemes>('/v1/storefront/theme/draft', body),
    discardStorefrontThemeDraft: () => api.delete<StorefrontThemes>('/v1/storefront/theme/draft'),
    publishStorefrontTheme: (body: StorefrontThemeBody) => api.post<StorefrontThemes>('/v1/storefront/theme/publish', body),
    restoreStorefrontTheme: (versionId: string) => api.post<StorefrontThemes>(`/v1/storefront/theme/versions/${versionId}/restore`),
    storefrontReviews: (status: string) => api.get<StorefrontReviews>(`/v1/storefront/reviews${status ? `?status=${status}` : ''}`),
    moderateStorefrontReview: (id: string, body: ModerateReviewBody) => api.put<{ ok: boolean }>(`/v1/storefront/reviews/${id}`, body),
    hideStorefrontReviewPhoto: (reviewId: string, photoId: string, body: HideReviewPhotoBody) =>
        api.put<{ ok: boolean }>(`/v1/storefront/reviews/${reviewId}/photos/${photoId}`, body),
    storefrontSubscribers: (query: SubscriberQuery = {}) => api.get<StorefrontSubscribers>(`/v1/storefront/subscribers${queryString(query)}`),
    withdrawSubscriber: (id: string) => api.post<void>(`/v1/storefront/subscribers/${id}/withdraw`),
    stockWaits: () => api.get<StockWaits>('/v1/storefront/stock-waits'),
    campaigns: () => api.get<Campaigns>('/v1/newsletter/campaigns'),
    createCampaign: (body: SaveCampaignBody) => api.post<Campaign>('/v1/newsletter/campaigns', body),
    updateCampaign: (id: string, body: SaveCampaignBody) => api.put<Campaign>(`/v1/newsletter/campaigns/${id}`, body),
    deleteCampaign: (id: string) => api.delete<void>(`/v1/newsletter/campaigns/${id}`),
    testCampaign: (id: string) => api.post<void>(`/v1/newsletter/campaigns/${id}/test`),
    sendCampaign: (id: string) => api.post<Campaign>(`/v1/newsletter/campaigns/${id}/send`),

    paymentProviders: () => api.get<PaymentProviders>('/v1/payment/providers'),
    paymentAccounts: () => api.get<PaymentAccounts>('/v1/payment/accounts'),
    connectPaymentService: (body: ConnectPaymentServiceBody) => api.post<PaymentAccount>('/v1/payment/accounts', body),
    verifyPaymentAccount: (id: string) => api.post<PaymentAccount>(`/v1/payment/accounts/${id}/verify`),
    deletePaymentAccount: (id: string) => api.delete<{ ok: boolean }>(`/v1/payment/accounts/${id}`),

    smsProviders: () => api.get<SmsProviders>('/v1/sms/providers'),
    smsAccounts: () => api.get<SmsAccounts>('/v1/sms/accounts'),
    connectSmsService: (body: ConnectSmsServiceBody) => api.post<SmsAccount>('/v1/sms/accounts', body),
    updateSmsAccount: (id: string, body: UpdateSmsAccountBody) => api.patch<SmsAccount>(`/v1/sms/accounts/${id}`, body),
    verifySmsAccount: (id: string) => api.post<SmsAccount>(`/v1/sms/accounts/${id}/verify`),
    sendTestSms: (id: string, body: SendTestSmsBody) => api.post<SmsMessage>(`/v1/sms/accounts/${id}/test`, body),
    deleteSmsAccount: (id: string) => api.delete<{ ok: boolean }>(`/v1/sms/accounts/${id}`),
    smsMessages: (query: SmsMessageQuery) => api.get<SmsMessages>(`/v1/sms/messages${queryString(query)}`),

    invoiceProviders: () => api.get<InvoiceProviders>('/v1/invoice/providers'),
    invoiceAccounts: () => api.get<InvoiceAccounts>('/v1/invoice/accounts'),
    connectInvoiceProvider: (body: ConnectInvoiceProviderBody) => api.post<InvoiceAccount>('/v1/invoice/accounts', body),
    verifyInvoiceAccount: (id: string) => api.post<InvoiceAccount>(`/v1/invoice/accounts/${id}/verify`),
    updateInvoiceAccount: (id: string, body: UpdateInvoiceAccountBody) => api.patch<InvoiceAccount>(`/v1/invoice/accounts/${id}`, body),
    rotateInvoiceCredentials: (id: string, body: RotateInvoiceCredentialsBody) =>
        api.put<{ ok: boolean }>(`/v1/invoice/accounts/${id}/credentials`, body),
    deleteInvoiceAccount: (id: string) => api.delete<{ ok: boolean }>(`/v1/invoice/accounts/${id}`),

    invoiceTemplates: () => api.get<InvoiceTemplates>('/v1/invoice/templates'),
    createInvoiceTemplate: (body: InvoiceTemplateBody) => api.post<InvoiceTemplate>('/v1/invoice/templates', body),
    updateInvoiceTemplate: (id: string, body: InvoiceTemplateBody) => api.put<InvoiceTemplate>(`/v1/invoice/templates/${id}`, body),
    deleteInvoiceTemplate: (id: string) => api.delete<{ ok: boolean }>(`/v1/invoice/templates/${id}`),

    listInvoices: (query: InvoiceQuery = {}) => api.get<InvoiceList>(`/v1/invoices${queryString(query)}`),
    issueManualInvoice: (body: ManualInvoiceBody) => api.post<InvoiceDetail>('/v1/invoices', body),
    getInvoice: (id: string) => api.get<InvoiceDetail>(`/v1/invoices/${id}`),
    orderInvoice: (orderId: string) => api.get<OrderInvoice>(`/v1/orders/${orderId}/invoice`),
    invoicePreview: (orderId: string, taxRate: number[] = []) =>
        api.get<InvoicePreview>(`/v1/orders/${orderId}/invoice-preview${queryString({ taxRate })}`),
    issueInvoice: (orderId: string, body: IssueInvoiceBody) => api.post<InvoiceDetail>(`/v1/orders/${orderId}/invoice`, body),
    cancelInvoice: (id: string, reason: string) => api.post<InvoiceDetail>(`/v1/invoices/${id}/cancel`, { reason }),
    recordInvoiceSerial: (id: string, body: RecordInvoiceSerialBody) => api.post<InvoiceDetail>(`/v1/invoices/${id}/serial`, body),
    uploadInvoiceDocument: (id: string, document: string) => api.post<InvoiceDetail>(`/v1/invoices/${id}/document`, { document }),
    invoiceDocumentLink: (id: string, format: InvoiceDocumentFormat) =>
        api.get<InvoiceDocumentLink>(`/v1/invoices/${id}/document${queryString({ format })}`),
    refreshInvoice: (id: string) => api.post<InvoiceDetail>(`/v1/invoices/${id}/refresh`),
    sendInvoiceToBuyer: (id: string, email?: string) => api.post<{ ok: boolean }>(`/v1/invoices/${id}/send`, email ? { email } : {}),
    invoiceExemptionCodes: () => api.get<InvoiceExemptionCodes>('/v1/invoice/exemption-codes'),
    creditNote: (id: string, reason: string) => api.post<InvoiceDetail>(`/v1/invoices/${id}/credit-note`, { reason }),

    dashboard: () => api.get<DashboardSummary>('/v1/dashboard'),
    setup: () => api.get<SetupChecklist>('/v1/setup'),
    dismissSetup: () => api.post<{ ok: boolean }>('/v1/setup/dismiss'),
    // Support access arrives from the platform panel as a single-use token. This
    // is the only call in the seller panel that can produce a session without a
    // password, which is why it does nothing else.
    redeemSupportSession: (token: string) => api.post<SessionPayload>('/v1/auth/support-session', { token }),

    search: (query: string) => api.get<SearchResults>(`/v1/search?q=${encodeURIComponent(query)}`),

    productImages: (productId: string) => api.get<ProductImages>(`/v1/products/${productId}/images`),
    uploadProductImage: (productId: string, file: File, alt: string) =>
        // Sent as its own bytes rather than a multipart form: one image per
        // request and no other fields, so multipart would be envelope with nothing
        // to carry.
        api.post<ProductImage>(`/v1/products/${productId}/images?alt=${encodeURIComponent(alt)}`, file, { headers: { 'Content-Type': file.type } }),
    detachProductImage: (productId: string, mediaId: string) => api.delete<{ ok: boolean }>(`/v1/products/${productId}/images/${mediaId}`),
    reorderProductImages: (productId: string, mediaIds: string[]) =>
        api.put<{ ok: boolean }>(`/v1/products/${productId}/images/order`, {
            mediaIds,
        }),
    lowStock: (query: LowStockQuery = {}) => api.get<LowStockList>(`/v1/stock/low${queryString(query)}`),
    bulkAdjustStock: (body: BulkStockBody) => api.post<BulkStockResult>('/v1/stock/bulk', body),
};
